package com.schoolconnect.app.feature.subjects

import com.schoolconnect.app.datasource.network.UserSessionStore
import com.schoolconnect.app.datasource.network.request.SubjectCreateRequest
import com.schoolconnect.app.datasource.network.response.serverTotalCountOrNull
import com.schoolconnect.app.datasource.network.request.SubjectUpdateRequest
import com.schoolconnect.app.datasource.network.request.SubjectFilterRequest
import com.schoolconnect.app.designsystem.appDateInputToApi
import com.schoolconnect.app.designsystem.AppToastCenter
import com.schoolconnect.app.feature.common.DebouncedActionRunner
import com.schoolconnect.app.feature.common.AdminBootstrapSequencer
import com.schoolconnect.app.feature.common.AdminPagedListCoordinator
import com.schoolconnect.app.feature.common.offset
import com.schoolconnect.app.feature.common.resolveServerPageLoadState
import com.schoolconnect.app.feature.common.withPageIndex
import com.schoolconnect.app.feature.common.withPageSize
import com.schoolconnect.app.feature.common.withTotals
import com.schoolconnect.app.feature.common.ServerSort
import com.schoolconnect.app.feature.common.ServerSortOrder
import com.schoolconnect.app.repository.subject.SubjectRepository
import com.schoolconnect.app.repository.systemlookup.SystemLookupRepository
import com.schoolconnect.app.repository.user.UserRepository
import com.schoolconnect.app.util.scLog
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.Job
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.cancel
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

class SubjectsViewModel(
    schoolId: String = ""
) {
    private companion object {
        const val TAG = "[DATA_RETRIEVE_SC] SubjectsViewModel"
    }

    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Default)
    private val reloadDebouncer = DebouncedActionRunner(scope)
    private val bootstrapSequencer = AdminBootstrapSequencer(scope, TAG)
    private val pagedListCoordinator = AdminPagedListCoordinator(reloadDebouncer, bootstrapSequencer)
    private var assignmentsJob: Job? = null
    private var subjectDetailJob: Job? = null
    private var categoryOptionsJob: Job? = null
    private val normalizedSchoolId = schoolId.trim().ifBlank { UserSessionStore.getHomeRoleSchoolId().trim() }
    private val _uiState = MutableStateFlow(
        SubjectsUiState(
            isLoading = normalizedSchoolId.isNotBlank()
        )
    )

    val uiState: StateFlow<SubjectsUiState> = _uiState.asStateFlow()

    init {
        if (normalizedSchoolId.isBlank()) {
            _uiState.update {
                it.copy(
                    isLoading = false,
                    errorMessage = "School context was not available for subject loading."
                )
            }
        } else {
            loadCategoryOptions()
            refreshSubjectsData(forceRemote = false)
        }
    }

    fun onEvent(event: SubjectsUiEvent) {
        when (event) {
            SubjectsUiEvent.BackClicked -> Unit
            is SubjectsUiEvent.SearchChanged -> updateSearchQuery(event.query, resetPage = true)
            is SubjectsUiEvent.CategoryChanged -> applyFilters(
                _uiState.value.filters.copy(category = event.category.trim())
            )
            is SubjectsUiEvent.SortChanged -> {
                _uiState.update {
                    val nextAscending = if (it.sortField == event.field) !it.sortAscending else true
                    it.copy(
                        sortField = event.field,
                        sortAscending = nextAscending,
                        paging = it.paging.withPageIndex(0)
                    )
                }
                refreshSubjectsPage(forceRemote = true)
            }
            is SubjectsUiEvent.PageChanged -> {
                _uiState.update { it.copy(paging = it.paging.withPageIndex(event.pageIndex)) }
                refreshSubjectsPage(forceRemote = false)
            }
            is SubjectsUiEvent.PageSizeChanged -> {
                _uiState.update { it.copy(paging = it.paging.withPageSize(event.pageSize)) }
                refreshSubjectsPage(forceRemote = false)
            }
            SubjectsUiEvent.FilterClicked -> _uiState.update {
                it.copy(
                    dialogMode = SubjectsDialogMode.FILTER,
                    filterDraft = it.filters,
                    errorMessage = "",
                    apiErrorMessage = "",
                    infoMessage = ""
                )
            }
            SubjectsUiEvent.FilterDismissed -> closeDialog()
            is SubjectsUiEvent.FilterApplied -> applyFilters(event.filters)
            SubjectsUiEvent.FilterReset -> {
                _uiState.update {
                    it.copy(
                        filters = SubjectFilterCriteria(),
                        filterDraft = SubjectFilterCriteria(),
                        searchQuery = "",
                        dialogMode = SubjectsDialogMode.NONE,
                        activeSubjectId = "",
                        paging = it.paging.withPageIndex(0),
                        errorMessage = "",
                        apiErrorMessage = "",
                        infoMessage = ""
                    )
                }
                refreshSubjectsPage(forceRemote = true)
            }
            SubjectsUiEvent.AddClicked -> _uiState.update {
                it.copy(
                    dialogMode = SubjectsDialogMode.EDITOR,
                    activeSubjectId = "",
                    isSaving = false,
                    editorDraft = SubjectEditorDraft(
                        schoolId = normalizedSchoolId
                    ),
                    errorMessage = "",
                    apiErrorMessage = "",
                    infoMessage = ""
                )
            }
            is SubjectsUiEvent.ViewClicked -> openDetails(event.subjectId)
            is SubjectsUiEvent.EditClicked -> openEditor(event.subjectId)
            is SubjectsUiEvent.DeleteClicked -> openDeleteConfirm(event.subjectId)
            SubjectsUiEvent.DeleteConfirmed -> confirmDelete()
            SubjectsUiEvent.DialogDismissed -> if (!_uiState.value.isSaving) closeDialog()
            SubjectsUiEvent.EditorSaveRequested -> if (!_uiState.value.isSaving) saveSubject()
            is SubjectsUiEvent.EditorCodeChanged -> _uiState.update {
                it.copy(editorDraft = it.editorDraft.copy(code = event.value.uppercase().take(SubjectCodeMaxLength)), apiErrorMessage = "")
            }
            is SubjectsUiEvent.EditorNameChanged -> _uiState.update {
                it.copy(editorDraft = it.editorDraft.copy(name = event.value), apiErrorMessage = "")
            }
            is SubjectsUiEvent.EditorCategoryChanged -> _uiState.update {
                it.copy(editorDraft = it.editorDraft.copy(category = event.value), apiErrorMessage = "")
            }
            SubjectsUiEvent.ClearMessages -> _uiState.update {
                it.copy(errorMessage = "", apiErrorMessage = "", infoMessage = "")
            }
        }
    }

    fun clear() {
        assignmentsJob?.cancel()
        subjectDetailJob?.cancel()
        categoryOptionsJob?.cancel()
        _uiState.update {
            it.copy(
                isLoading = false,
                isSaving = false,
                apiErrorMessage = ""
            )
        }
        pagedListCoordinator.clear()
        scope.cancel()
    }

    private fun loadCategoryOptions() {
        categoryOptionsJob?.cancel()
        categoryOptionsJob = scope.launch {
            SystemLookupRepository.loadCategoryItems("SUBJECT_CATEGORY")
                .fold(
                    onSuccess = { items ->
                        val options = items.mapNotNull { item ->
                            (item.displayValue ?: item.code)?.trim()?.takeIf { it.isNotBlank() }
                        }.distinct()
                        _uiState.update {
                            it.copy(
                                categoryOptions = options,
                                errorMessage = if (options.isEmpty()) {
                                    "No subject categories were returned by the server."
                                } else {
                                    it.errorMessage
                                }
                            )
                        }
                    },
                    onFailure = { throwable ->
                        scLog("$TAG categoryOptionsFailure", throwable)
                        _uiState.update {
                            it.copy(errorMessage = "Subject categories could not be loaded. Please retry.")
                        }
                    }
                )
        }
    }

    private fun updateSearchQuery(
        query: String,
        resetPage: Boolean
    ) {
        _uiState.update {
            it.copy(
                searchQuery = query,
                paging = if (resetPage) it.paging.withPageIndex(0) else it.paging,
                errorMessage = "",
                infoMessage = ""
            )
        }
        scheduleSubjectsReload()
    }

    private fun applyFilters(filters: SubjectFilterCriteria) {
        _uiState.update {
            it.copy(
                filters = filters,
                filterDraft = filters,
                dialogMode = SubjectsDialogMode.NONE,
                activeSubjectId = "",
                paging = it.paging.withPageIndex(0),
                errorMessage = "",
                infoMessage = ""
            )
        }
        refreshSubjectsPage(forceRemote = true)
    }

    private fun scheduleSubjectsReload(immediate: Boolean = false) {
        pagedListCoordinator.schedulePage(immediate = immediate) { pageForceRemote ->
            loadSubjectsPage(pageForceRemote)
        }
    }

    private fun refreshSubjectsData(forceRemote: Boolean) {
        _uiState.update {
            it.copy(
                isLoading = true,
                errorMessage = "",
                infoMessage = ""
            )
        }
        pagedListCoordinator.refreshPage(forceRemote = forceRemote) { pageForceRemote ->
            loadSubjectsPage(pageForceRemote)
        }
    }

    private fun refreshSubjectsPage(forceRemote: Boolean) {
        pagedListCoordinator.refreshPage(forceRemote = forceRemote) { pageForceRemote ->
            loadSubjectsPage(pageForceRemote)
        }
    }

    private suspend fun loadSubjectsPage(forceRemote: Boolean) {
        val currentState = _uiState.value
        val paging = currentState.paging
        val query = currentState.searchQuery.trim()
        val category = currentState.filters.category.trim().takeIf { it.isNotBlank() }
        val isActive = currentState.filters.status.toSubjectStatusBoolean()
        val advancedFilter = SubjectFilterRequest(
            subjectCode = currentState.filters.subjectCode.trim().ifBlank { null },
            subjectName = currentState.filters.subjectName.trim().ifBlank { null },
            category = category,
            status = currentState.filters.status.trim().ifBlank { null },
            createdDateFrom = appDateInputToApi(currentState.filters.createdFrom).orEmpty().ifBlank { null },
            createdDateTo = appDateInputToApi(currentState.filters.createdTo).orEmpty().ifBlank { null }
        ).takeIf { it.subjectCode != null || it.subjectName != null || it.createdDateFrom != null || it.createdDateTo != null }

        scLog(
            "$TAG refreshSubjectsPage start q=${query.ifBlank { "<blank>" }} category=${category ?: "<any>"} status=${currentState.filters.status.ifBlank { "<any>" }} pageIndex=${paging.pageIndex} pageSize=${paging.pageSize} offset=${paging.offset()} forceRemote=$forceRemote"
        )
        _uiState.update {
            it.copy(
                isLoading = true,
                errorMessage = "",
                infoMessage = ""
            )
        }

        val result = SubjectRepository.loadSchoolSubjectsPage(
            schoolId = normalizedSchoolId,
            query = query,
            category = category,
            isActive = isActive,
            advancedFilter = advancedFilter,
            pageIndex = paging.pageIndex,
            pageSize = paging.pageSize,
            sort = ServerSort(
                field = currentState.sortField.apiField,
                order = if (currentState.sortAscending) ServerSortOrder.ASC else ServerSortOrder.DESC
            ),
            forceRemote = forceRemote
        )

        result.fold(
            onSuccess = { rows ->
                val serverTotalCount = rows.serverTotalCountOrNull()
                val mappedRows = rows
                    .distinctBy { it.id }
                    .map { it.toRowUi() }

                val pageLoad = resolveServerPageLoadState(
                    exactTotal = serverTotalCount,
                    paging = paging,
                    currentItemCount = mappedRows.size,
                    pageOffset = paging.offset()
                )
                if (pageLoad.clampedPageIndex != pageLoad.paging.pageIndex) {
                    _uiState.update { current ->
                        current.copy(
                            paging = current.paging.withPageIndex(pageLoad.clampedPageIndex),
                            isLoading = false
                        )
                    }
                    refreshSubjectsPage(forceRemote = forceRemote)
                    return@fold
                }

                _uiState.update { current ->
                    current.copy(
                        isLoading = false,
                        subjects = mappedRows,
                        paging = pageLoad.paging,
                        errorMessage = "",
                        infoMessage = current.infoMessage
                    )
                }
                scLog(
                    "$TAG refreshSubjectsPage success school=$normalizedSchoolId count=${mappedRows.size} totalCount=${pageLoad.paging.totalCount} exact=${pageLoad.paging.isTotalCountExact}"
                )
            },
            onFailure = { throwable ->
                if (throwable is CancellationException) {
                    scLog("$TAG refreshSubjectsPage cancelled school=$normalizedSchoolId")
                } else {
                    scLog("$TAG refreshSubjectsPage failure school=$normalizedSchoolId", throwable)
                    _uiState.update {
                        it.copy(
                            isLoading = false,
                            errorMessage = throwable.message.orEmpty().ifBlank { "Unable to load subjects." }
                        )
                    }
                }
            }
        )
    }

    private fun confirmDelete() {
        val subjectId = _uiState.value.activeSubjectId
        val subject = _uiState.value.subjects.firstOrNull { it.id == subjectId } ?: run {
            _uiState.update {
                it.copy(errorMessage = "Subject not found.", infoMessage = "")
            }
            return
        }

        _uiState.update {
            it.copy(
                isLoading = true,
                isSaving = true,
                errorMessage = "",
                infoMessage = ""
            )
        }

        scope.launch {
            try {
                SubjectRepository.deleteSubject(subject.id).getOrThrow()
                refreshSubjectsData(forceRemote = true)
                _uiState.update {
                    it.copy(
                        isLoading = false,
                        isSaving = false,
                        dialogMode = SubjectsDialogMode.NONE,
                        activeSubjectId = "",
                        infoMessage = "Deleted ${subject.code}"
                    )
                }
                scLog("$TAG deleteSubject success school=$normalizedSchoolId subjectId=${subject.id}")
            } catch (throwable: Throwable) {
                if (throwable is CancellationException) {
                    scLog("$TAG deleteSubject cancelled school=$normalizedSchoolId subjectId=${subject.id}")
                } else {
                    scLog("$TAG deleteSubject failure school=$normalizedSchoolId subjectId=${subject.id}", throwable)
                    _uiState.update {
                        it.copy(
                            isLoading = false,
                            isSaving = false,
                            errorMessage = throwable.message.orEmpty().ifBlank { "Failed to delete subject." }
                        )
                    }
                }
            }
        }
    }

    private fun openEditor(subjectId: String) {
        val subject = _uiState.value.subjects.firstOrNull { it.id == subjectId }
        _uiState.update {
            it.copy(
                dialogMode = if (subject == null) SubjectsDialogMode.NONE else SubjectsDialogMode.EDITOR,
                activeSubjectId = subjectId,
                editorDraft = subject?.toDraft() ?: SubjectEditorDraft(schoolId = normalizedSchoolId),
                isSaving = false,
                errorMessage = subject?.let { "" } ?: "Subject not found",
                apiErrorMessage = "",
                infoMessage = ""
            )
        }
    }

    private fun openDetails(subjectId: String) {
        val row = _uiState.value.subjects.firstOrNull { it.id == subjectId }
        if (row == null) {
            _uiState.update { it.copy(errorMessage = "Subject not found.", infoMessage = "") }
            return
        }
        _uiState.update {
            it.copy(
                dialogMode = SubjectsDialogMode.DETAILS,
                activeSubjectId = row.id,
                errorMessage = "",
                infoMessage = ""
            )
        }
        subjectDetailJob?.cancel()
        subjectDetailJob = scope.launch {
            SubjectRepository.loadSubjectById(row.id, forceRemote = true).fold(
                onSuccess = { response ->
                    val detailRow = response.toRowUi().let { row ->
                        row.copy(
                            createdBy = row.createdBy.ifBlank {
                                response.createdById.orEmpty()
                                    .takeIf { it.isNotBlank() }
                                    ?.let { UserRepository.loadUserDisplayName(it).getOrNull().orEmpty() }
                                    .orEmpty()
                            },
                            updatedBy = row.updatedBy.ifBlank {
                                response.updatedById.orEmpty()
                                    .takeIf { it.isNotBlank() }
                                    ?.let { UserRepository.loadUserDisplayName(it).getOrNull().orEmpty() }
                                    .orEmpty()
                            }
                        )
                    }
                    _uiState.update { state ->
                        if (state.dialogMode != SubjectsDialogMode.DETAILS || state.activeSubjectId != row.id) {
                            state
                        } else {
                            state.copy(
                                subjects = state.subjects.map { current ->
                                    if (current.id == row.id) detailRow else current
                                }
                            )
                        }
                    }
                },
                onFailure = { throwable ->
                    scLog("$TAG load subject details failed subjectId=${row.id}", throwable)
                }
            )
        }
    }

    private fun openDeleteConfirm(subjectId: String) {
        val subject = _uiState.value.subjects.firstOrNull { it.id == subjectId }
        if (subject == null) {
            _uiState.update { it.copy(errorMessage = "Subject not found.", infoMessage = "") }
            return
        }

        assignmentsJob?.cancel()
        _uiState.update {
            it.copy(
                dialogMode = SubjectsDialogMode.DELETE_CONFIRM,
                activeSubjectId = subjectId,
                assignedClasses = emptyList(),
                isAssignmentsLoading = true,
                assignmentsErrorMessage = "",
                errorMessage = "",
                infoMessage = ""
            )
        }

        assignmentsJob = scope.launch {
            SubjectRepository.loadSubjectClassAssignments(
                schoolId = normalizedSchoolId,
                subjectCode = subject.code,
                forceRemote = true
            ).fold(
                onSuccess = { assignments ->
                    _uiState.update {
                        it.copy(
                            assignedClasses = assignments.map { (className, sectionName) ->
                                SubjectClassAssignmentUi(className, sectionName)
                            },
                            isAssignmentsLoading = false
                        )
                    }
                },
                onFailure = { throwable ->
                    if (throwable !is CancellationException) {
                        _uiState.update {
                            it.copy(
                                isAssignmentsLoading = false,
                                assignmentsErrorMessage = throwable.message.orEmpty()
                                    .ifBlank { "Unable to check class assignments." }
                            )
                        }
                    }
                }
            )
        }
    }

    private fun closeDialog() {
        assignmentsJob?.cancel()
        assignmentsJob = null
        _uiState.update {
            it.copy(
                dialogMode = SubjectsDialogMode.NONE,
                activeSubjectId = "",
                assignedClasses = emptyList(),
                isAssignmentsLoading = false,
                assignmentsErrorMessage = "",
                isLoading = false,
                isSaving = false,
                editorDraft = it.editorDraft.copy(),
                filterDraft = it.filterDraft.copy(),
                errorMessage = "",
                apiErrorMessage = "",
                infoMessage = ""
            )
        }
    }

    private fun saveSubject() {
        _uiState.update { it.copy(apiErrorMessage = "") }
        val draft = _uiState.value.editorDraft
        val normalizedCode = draft.code.trim().uppercase()
        val normalizedName = draft.name.trim()
        val normalizedSchool = draft.schoolId.trim().ifBlank { normalizedSchoolId }
        val normalizedCategory = draft.category.trim()
        if (normalizedSchool.isBlank() || normalizedCode.isBlank() || normalizedName.isBlank() || normalizedCategory.isBlank()) {
            _uiState.update {
                it.copy(errorMessage = "Enter subject code, name and category.", infoMessage = "")
            }
            return
        }

        val isCreate = draft.id.isBlank()
        _uiState.update {
                it.copy(
                    isLoading = true,
                    isSaving = true,
                    errorMessage = "",
                    apiErrorMessage = "",
                    infoMessage = ""
            )
        }

        scope.launch {
            try {
                if (isCreate) {
                    SubjectRepository.createSubject(
                        schoolId = normalizedSchool,
                        request = SubjectCreateRequest(
                            name = normalizedName,
                            shortCode = normalizedCode,
                            category = normalizedCategory,
                            isActive = true
                        )
                    ).getOrThrow()
                } else {
                    SubjectRepository.updateSubject(
                        subjectId = draft.id,
                        request = SubjectUpdateRequest(
                            schoolId = normalizedSchool,
                            name = normalizedName,
                            shortCode = normalizedCode,
                            category = normalizedCategory,
                            isActive = null
                        )
                    ).getOrThrow()
                }

                _uiState.update {
                    it.copy(
                        isLoading = false,
                        isSaving = false,
                        dialogMode = SubjectsDialogMode.NONE,
                        activeSubjectId = "",
                        errorMessage = "",
                        apiErrorMessage = "",
                        infoMessage = ""
                    )
                }
                SubjectRepository.invalidateCaches()
                AppToastCenter.showSuccess(if (isCreate) "Created $normalizedCode" else "Updated $normalizedCode")
                refreshSubjectsData(forceRemote = true)
                scLog(
                    "$TAG saveSubject success school=$normalizedSchoolId subjectId=${draft.id.ifBlank { normalizedCode }} mode=${if (isCreate) "create" else "update"}"
                )
            } catch (throwable: Throwable) {
                if (throwable is CancellationException) {
                    scLog("$TAG saveSubject cancelled school=$normalizedSchoolId subjectId=${draft.id.ifBlank { normalizedCode }}")
                } else {
                    scLog("$TAG saveSubject failure school=$normalizedSchoolId subjectId=${draft.id.ifBlank { normalizedCode }}", throwable)
                    _uiState.update {
                        it.copy(
                            isLoading = false,
                            isSaving = false,
                            apiErrorMessage = throwable.message.orEmpty().ifBlank { "Failed to save subject." }
                        )
                    }
                }
            }
        }
    }
}

private fun String.toSubjectStatusBoolean(): Boolean? = when (trim().lowercase()) {
    "active" -> true
    "inactive" -> false
    else -> null
}
